import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useSeamHttpClient } from "api-hooks/use-seam-client"

interface Params {
  workspace_id?: string
}

type TemperatureUnits = "f" | "c"

export interface AssaAbloyCredentials {
  application_id: string
}

export interface NestCredentials {
  project_id: string
  client_id: string
  client_secret: string
}

type WorkspaceSettingsPatch = {
  temperature_unit?: TemperatureUnits
  // Battery level (0–1) below which low-battery alerts fire. `null` clears a
  // previously set value back to Seam defaults.
  low_battery_alert_threshold?: number | null
  // Stops the device-oriented access code lifecycle events, and the error /
  // warning codes paired with them, in favour of the intent-oriented ones.
  is_legacy_access_code_lifecycle_retired?: boolean
  // ASSA ABLOY / Visionline credentials for custom mobile apps. `null` clears.
  assa_abloy?: AssaAbloyCredentials | null
  // Google Nest developer credentials. `null` clears.
  nest?: NestCredentials | null
}

export const WORKSPACE_SETTINGS_QK = (workspace_id?: string) =>
  ["workspace_settings", workspace_id] as const

export const useWorkspaceSettings = ({ workspace_id }: Params) => {
  const seam = useSeamHttpClient(workspace_id)
  return useQuery({
    queryKey: ["workspace_settings", workspace_id],
    queryFn: () =>
      seam?.client
        .get("/internal/workspaces/get_workspace_settings")
        .then((r) => r.data.workspace_settings),
    enabled: seam != null,
  })
}

export function useUpdateWorkspaceSettings(workspace_id?: string) {
  const seam = useSeamHttpClient(workspace_id)
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (patch: WorkspaceSettingsPatch) => {
      await seam?.client.patch(
        "/internal/workspaces/update_workspace_settings",
        {
          workspace_settings: patch,
        }
      )
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WORKSPACE_SETTINGS_QK(workspace_id) })
    },
  })
}
